import { useCallback, useEffect, useSyncExternalStore } from "react";
import { useWebSocket } from "../context/WebSocketContext";
import { logSecretly } from "../utils/logSecretly";
import { WS_EVENTS } from "../constants";
import {
  applyPodSessionReport,
  clearPodSessionReport,
  getPodSessionReport,
  subscribePodSessionReport,
  type PodSessionReport,
} from "../utils/podSessionReport";
import type { PodSessionReportPayload } from "../dtos/websocket";

/**
 * Listens for the WebSocket PodSessionReport event and stores the result
 * against the previous session. Mount once, app-wide: the event lands after
 * the session ended, so no session-scoped screen is alive to receive it.
 */
export function usePodSessionReportListener(): void {
  const { subscribe } = useWebSocket();

  useEffect(() => {
    const unsubscribe = subscribe(
      WS_EVENTS.POD_SESSION_REPORT,
      (payload: PodSessionReportPayload) => {
        logSecretly("Received PodSessionReport event:", payload);
        applyPodSessionReport(payload);
      }
    );

    return () => unsubscribe();
  }, [subscribe]);
}

interface UsePodSessionReportResult {
  report: PodSessionReport | null;
  dismiss: () => void;
}

/**
 * Reads the stored post-session report and keeps it in sync with the store,
 * including writes from another tab. The wait has no time limit — it ends when
 * the report arrives, or when the user dismisses it.
 */
export function usePodSessionReport(): UsePodSessionReportResult {
  const report = useSyncExternalStore(
    subscribePodSessionReport,
    getPodSessionReport
  );

  const dismiss = useCallback(() => clearPodSessionReport(), []);

  return { report, dismiss };
}
